import NoscriptImageHelper from './NoscriptImageHelper';

/**
 * Smooth scrolling, header offset, and anchor-link helpers.
 */
class ScrollToHelper {
    /**
     * Smooth-scroll to an element (or top), optionally subtracting spacing.
     *
     * @param {HTMLElement|null} [element=null] Target element; null scrolls to top
     * @param {number} [additionalSpacing] Extra offset (defaults to header height)
     */
    static scrollTo(element = null, additionalSpacing = ScrollToHelper.getHeaderHeight()) {
        let scrollTop = window.pageYOffset;

        if (window.pageYOffset === undefined) {
            [scrollTop] = (document.documentElement || document.body.parentNode || document.body);
        }

        let top = element !== null ? element.getBoundingClientRect().top + scrollTop : 0;

        if (additionalSpacing !== 0) {
            top -= additionalSpacing;
        }

        window.scrollTo({
            behavior: 'smooth',
            left: 0,
            top,
        });
    }

    /**
     * Document-relative top offset of an element (or `0` if null).
     *
     * @param {HTMLElement|null} [element=null]
     * @returns {number}
     */
    static getElementTopOffset(element = null) {
        let scrollTop = window.pageYOffset;

        if (window.pageYOffset === undefined) {
            [scrollTop] = (document.documentElement || document.body.parentNode || document.body);
        }

        return element !== null ? element.getBoundingClientRect().top + scrollTop : 0;
    }

    /**
     * Height of the `.page-header` element.
     *
     * @returns {number}
     */
    static getHeaderHeight() {
        return document.querySelector('.page-header').getBoundingClientRect().height;
    }

    /**
     * Bind click handlers on in-page hash links for smooth-scroll with header offset.
     *
     * Picture `width`/`height` reserve space, so intervening lazy images no longer
     * shift the target. Leftover `[data-noscript]` markup (cached pages) is unwrapped first.
     *
     * TYPO3 spam-protected emails render as `a[href="#"][data-mailto-token]`. Those must
     * not be treated as anchors: an unconditional `preventDefault()` would cancel
     * `default_frontend.js`, which decrypts the token and assigns `mailto:`.
     */
    static initializeAnchorLinks() {
        const links = document.querySelectorAll('a[href^="#"]:not([data-mailto-token])');
        links.forEach((link) => {
            link.addEventListener('click', (event) => {
                const href = event.currentTarget.getAttribute('href') || '';

                if (href.length <= 1 || !href.startsWith('#')) {
                    return;
                }

                event.preventDefault();

                const targetId = href.replace(/^#/, '');
                const targetElement = document.getElementById(targetId);

                if (document.querySelector('[data-noscript]')) {
                    NoscriptImageHelper.unwrapAll();
                }

                window.requestAnimationFrame(() => {
                    ScrollToHelper.scrollTo(targetElement);
                });
            });
        });
    }
}

export default ScrollToHelper;
